/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {Pipe, PipeTransform} from '@angular/core';
import {BaseException} from '../facade/exceptions';
import {NumberFormatStyle, NumberFormatter} from '../facade/intl';
import {NumberWrapper, RegExpWrapper, Type, isBlank, isNumber, isPresent} from '../facade/lang';
import {InvalidPipeArgumentException} from './invalid_pipe_argument_exception';

var defaultLocale: string = 'en-US';
const _NUMBER_FORMAT_REGEXP = /^(\d+)?\.((\d+)(\-(\d+))?)?$/g;

/**
 * Internal function to format numbers used by Decimal, Percent and Date pipes.
 */
function formatNumber(
    pipe: Type, value: number, style: NumberFormatStyle, digits: string, currency: string = null,
    currencyAsSymbol: boolean = false): string {
  if (isBlank(value)) return null;
  if (!isNumber(value)) {
    throw new InvalidPipeArgumentException(pipe, value);
  }
  var minInt = 1, minFraction = 0, maxFraction = 3;
  if (isPresent(digits)) {
    var parts = RegExpWrapper.firstMatch(_NUMBER_FORMAT_REGEXP, digits);
    if (isBlank(parts)) {
      throw new BaseException(`${digits} is not a valid digit info for number pipes`);
    }
    if (isPresent(parts[1])) {  // min integer digits
      minInt = NumberWrapper.parseIntAutoRadix(parts[1]);
    }
    if (isPresent(parts[3])) {  // min fraction digits
      minFraction = NumberWrapper.parseIntAutoRadix(parts[3]);
    }
    if (isPresent(parts[5])) {  // max fraction digits
      maxFraction = NumberWrapper.parseIntAutoRadix(parts[5]);
    }
  }
  return NumberFormatter.format(value, defaultLocale, style, {
    minimumIntegerDigits: minInt,
    minimumFractionDigits: minFraction,
    maximumFractionDigits: maxFraction,
    currency: currency,
    currencyAsSymbol: currencyAsSymbol
  });
}
export class DecimalPipe implements PipeTransform {
  transform(value: any, digits: string = null): string {
    return formatNumber(DecimalPipe, value, NumberFormatStyle.Decimal, digits);
  }
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Pipe, args: [{name: 'number'}, ] },
];
}
export class PercentPipe implements PipeTransform {
  transform(value: any, digits: string = null): string {
    return formatNumber(PercentPipe, value, NumberFormatStyle.Percent, digits);
  }
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Pipe, args: [{name: 'percent'}, ] },
];
}
export class CurrencyPipe implements PipeTransform {
  transform(
      value: any, currencyCode: string = 'USD', symbolDisplay: boolean = false,
      digits: string = null): string {
    return formatNumber(
        CurrencyPipe, value, NumberFormatStyle.Currency, digits, currencyCode, symbolDisplay);
  }
/** @nocollapse */
static decorators: DecoratorInvocation[] = [
{ type: Pipe, args: [{name: 'currency'}, ] },
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
